import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IDeleteBillBeneficiaryRequest {
  beneficiaryId: string;
}
interface IDeleteBillBeneficiaryResponse {
  status?: boolean;
  message?: string;
  errors?: IApiResponseError[];
}

const initialState: IApiState<IDeleteBillBeneficiaryResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const deleteBillBeneficiaryAsync = createAsyncThunk(
  'deleteBillBeneficiary',
  async (
    { beneficiaryId }: IDeleteBillBeneficiaryRequest,
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.delete<IDeleteBillBeneficiaryResponse>(
        `/billpayment/beneficiaries/delete/${beneficiaryId}`,
        config
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IDeleteBillBeneficiaryResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const deleteBillBeneficiarySlice = createSlice({
  name: 'deleteBillBeneficiary',
  initialState,
  reducers: {
    resetBillBeneficiaryDelete(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteBillBeneficiaryAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(deleteBillBeneficiaryAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IDeleteBillBeneficiaryResponse;
      })
      .addCase(deleteBillBeneficiaryAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IDeleteBillBeneficiaryResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetBillBeneficiaryDelete } =
  deleteBillBeneficiarySlice.actions;

export default deleteBillBeneficiarySlice.reducer;
